import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import { formatError } from "@/lib/api";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import Brand from "@/components/common/Brand";

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, verify2faLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // iter154 · Sidebar-Login-Fix: /login akzeptiert ?email=xxx als Prefill für 2FA-Weiterleitung
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preEmail = params.get("email");
    if (preEmail && !email) setEmail(preEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Sub-Batch 2FA-1: second step state
  const [tfaChallenge, setTfaChallenge] = useState(null); // { challenge_token, email }
  const [tfaCode, setTfaCode] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data && data.require_2fa) {
        setTfaChallenge({ challenge_token: data.challenge_token, email: data.email });
      } else if (data.profile_completed) {
        navigate("/");
      } else {
        navigate("/onboarding/profile");
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleTfaSubmit = async (e) => {
    e.preventDefault();
    if (!tfaChallenge) return;
    setError("");
    setLoading(true);
    try {
      const data = await verify2faLogin(tfaChallenge.challenge_token, tfaCode.trim());
      if (data.profile_completed) {
        navigate("/");
      } else {
        navigate("/onboarding/profile");
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="login-page">
      {/* Header */}
      <div className="h-14 bg-[#0A0A0A] flex items-center justify-between px-4">
        <button data-testid="login-back-btn" onClick={() => navigate("/")} className="text-white/70 hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
        <Brand className="text-xl" />
        <LanguageSwitcher compact />
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 max-w-md mx-auto w-full">
        {!tfaChallenge ? (
          <>
            <h1 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">{t("login.title")}</h1>
            <p className="text-sm text-[#999] mt-1">{t("login.welcome_back")}</p>

            {error && (
              <InfoCard variant="danger" data-testid="login-error" size="sm">{error}</InfoCard>
            )}

            <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
              <div>
                <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("login.email")}</label>
                <input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full h-12 px-4 rounded-full border border-[#E5E5E5] bg-white text-sm text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] transition-shadow"
                  placeholder={t("login.email_placeholder")}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("login.password")}</label>
                <div className="relative mt-1">
                  <input
                    data-testid="login-password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-12 px-4 pr-12 rounded-full border border-[#E5E5E5] bg-white text-sm text-[#0A0A0A] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] transition-shadow"
                    placeholder={t("login.password_placeholder")}
                    required
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-3 text-[#999]">
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-[#999] hover:text-[#0A0A0A] transition-colors">
                  {t("login.forgot")}
                </Link>
              </div>
              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-medium transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-50"
              >
                {loading ? t("common.loading") : t("login.login_btn")}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-[#999]">
              {t("login.no_account")}{" "}
              <Link to="/register" data-testid="register-link" className="text-[#0A0A0A] font-medium hover:underline">
                {t("login.register")}
              </Link>
            </p>
            <p className="mt-3 text-center">
              <Link to="/tracking" data-testid="guest-tracking-link" className="text-xs text-[#999] hover:text-[#0A0A0A] transition-colors">
                {t("login.guest_tracking")}
              </Link>
            </p>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-[#0A0A0A]" />
              <h1 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">{t("login.tfa_title")}</h1>
            </div>
            <p className="text-sm text-[#999] mt-1">{t("login.tfa_desc")}</p>

            {error && (
              <InfoCard variant="danger" data-testid="login-2fa-error" size="sm">{error}</InfoCard>
            )}

            <form onSubmit={handleTfaSubmit} className="mt-6 flex flex-col gap-4">
              <div>
                <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("login.tfa_code")}</label>
                <input
                  data-testid="login-2fa-code"
                  type="text"
                  value={tfaCode}
                  onChange={(e) => setTfaCode(e.target.value)}
                  className="mt-1 w-full h-12 px-4 rounded-full border border-[#E5E5E5] bg-white text-sm text-[#0A0A0A] text-center tracking-[0.4em] font-mono focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                  placeholder="123456"
                  autoFocus
                  autoComplete="one-time-code"
                  required
                />
              </div>
              <button
                data-testid="login-2fa-submit"
                type="submit"
                disabled={loading || tfaCode.trim().length < 6}
                className="h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-medium transition-colors hover:bg-[#222] disabled:opacity-50"
              >
                {loading ? t("login.tfa_checking") : t("login.tfa_confirm")}
              </button>
              <button
                type="button"
                data-testid="login-2fa-cancel"
                onClick={() => { setTfaChallenge(null); setTfaCode(""); setError(""); }}
                className="h-10 rounded-full border border-[#E5E5E5] text-sm text-[#666] hover:bg-[#F5F5F5]"
              >
                {t("login.tfa_cancel")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
